import { Dispatch, SetStateAction } from 'react';
import { FeatureSwitches } from '../../../constants/feature-switches';
import { IPHRequest } from '../../../models/purchase-history';
import { getWindow } from '../../../utils/general';

export const PHTableHeaderData = (
  isAdminRole: boolean,
  pHRequest: IPHRequest,
  searchByObj: {
    [key: string]: string;
  },
  selectedHeader: string | undefined,
  setPHRequest: Dispatch<SetStateAction<IPHRequest>>,
  sortDirection: 'desc' | 'asc' | undefined,
  tableHeaderState: Array<{
    enabled: boolean;
  }>,
) => {
  const lineItemData = Object.prototype.hasOwnProperty.call(
    searchByObj,
    'itemDescription',
  );

  if (lineItemData) {
    // Line Item Data
    return [
      {
        title: 'Date',
        style: { width: '90px' },
        sort: false,
        sortProps: {
          sortDirection: '',
          ariaLabel: '',
          active: false,
          direction: undefined,
          padding: '',
          onClick: () => null,
          style: {},
        },
        showHeader: true,
      },
      {
        title: 'Item',
        style: { width: '70px' },
        sort: false,
        showHeader: true,
      },
      {
        title: 'Description',
        style: { width: '155px' },
        sort: false,
        showHeader: true,
      },
      {
        title: 'Order #',
        style: { width: '119px' },
        sort: false,
        showHeader: true,
      },
      {
        title: 'Receipt #',
        style: { width: '119px' },
        sort: false,
        showHeader: true,
      },
      {
        title: 'Qty',
        style: { width: '48px' },
        sort: false,
        showHeader: true,
      },
      {
        title: 'Unit Price',
        style: { width: '106px' },
        sort: false,
        showHeader: true,
      },
      {
        title: 'Discount',
        style: { width: '89px' },
        sort: false,
        showHeader: true,
      },
      {
        title: 'Net Unit Price',
        style: { width: '115px' },
        sort: false,
        showHeader: true,
      },
    ];
  }

  return [
    {
      title: 'Products',
      style: { minWidth: '132px' },
      sort: false,
      showHeader: tableHeaderState?.[0]?.enabled,
    },
    {
      title: 'Order/\nReceipt #',
      style: { minWidth: '132px' },
      sort: false,
      sortProps: {
        sortDirection:
          selectedHeader === 'orderNumber' ? sortDirection : undefined,
        ariaLabel: 'Sort by order number',
        active: selectedHeader === 'orderNumber',
        direction: sortDirection,
        padding: 'none',
        onClick: () => {
          setPHRequest({
            ...pHRequest,
            sortBy: 'orderNumber',
            sortOrder: sortDirection === 'asc' ? 'desc' : 'asc',
          });
        },
        style: { paddingLeft: '0px' },
      },
      showHeader: tableHeaderState?.[1]?.enabled,
    },
    {
      title: 'Date',
      style: { minWidth: '100px' },
      sort: true,
      sortProps: {
        sortDirection:
          selectedHeader === 'salesDate' ? sortDirection : undefined,
        ariaLabel: 'Sort by sales date',
        active: selectedHeader === 'salesDate',
        direction: sortDirection,
        padding: 'none',
        onClick: () => {
          setPHRequest({
            ...pHRequest,
            sortBy: 'salesDate',
            sortOrder: sortDirection === 'asc' ? 'desc' : 'asc',
          });
        },
        style: { paddingLeft: '0px', paddingRight: '34px' },
      },
      showHeader: tableHeaderState?.[2]?.enabled,
    },
    {
      title: 'Origin',
      style: { minWidth: '101px' },
      sort: false,
      sortProps: {
        sortDirection:
          selectedHeader === 'storeNumber' ? sortDirection : undefined,
        ariaLabel: 'Sort by store number',
        active: selectedHeader === 'storeNumber',
        direction: sortDirection,
        padding: 'none',
        onClick: () => {
          setPHRequest({
            ...pHRequest,
            sortBy: 'storeNumber',
            sortOrder: sortDirection === 'asc' ? 'desc' : 'asc',
          });
        },
        style: { paddingLeft: '0px' },
      },
      showHeader: tableHeaderState?.[3]?.enabled,
    },
    {
      title: 'Project name',
      style: { minWidth: '110px' },
      sort: false,
      sortProps: {
        sortDirection:
          selectedHeader === 'projectName' ? sortDirection : undefined,
        ariaLabel: 'Sort by project name',
        active: selectedHeader === 'projectName',
        direction: sortDirection,
        padding: 'none',
        onClick: () => {
          setPHRequest({
            ...pHRequest,
            sortBy: 'projectName',
            sortOrder: sortDirection === 'asc' ? 'desc' : 'asc',
          });
        },
        style: { paddingLeft: '0px', textAlign: 'left' },
      },
      showHeader: tableHeaderState?.[4]?.enabled ?? true,
    },
    {
      title: 'PO/Job \nName',
      style: { minWidth: '124px' },
      sort: false,
      sortProps: {
        sortDirection:
          selectedHeader === 'POJobName' ? sortDirection : undefined,
        ariaLabel: 'Sort by POJobName',
        active: selectedHeader === 'POJobName',
        direction: sortDirection,
        padding: 'none',
        onClick: () => {
          setPHRequest({
            ...pHRequest,
            sortBy: 'POJobName',
            sortOrder: sortDirection === 'asc' ? 'desc' : 'asc',
          });
        },
        style: { paddingLeft: '0px', textAlign: 'left' },
      },
      showHeader: tableHeaderState?.[5]?.enabled,
    },
    {
      title: 'Payment',
      style: { minWidth: '128px' },
      sort: false,
      sortProps: {
        sortDirection:
          selectedHeader === 'totalAmount' ? sortDirection : undefined,
        ariaLabel: 'Sort by total amount',
        active: selectedHeader === 'totalAmount',
        direction: sortDirection,
        padding: 'none',
        onClick: () => {
          setPHRequest({
            ...pHRequest,
            sortBy: 'totalAmount',
            sortOrder: sortDirection === 'asc' ? 'desc' : 'asc',
          });
        },
        style: { paddingLeft: '0px' },
      },
      showHeader: tableHeaderState?.[6]?.enabled,
    },
    {
      title: 'Purchaser',
      style: { minWidth: '112px', textAlign: 'left' },
      sort: false,
      isAdmin: isAdminRole,
      showHeader: tableHeaderState?.[7]?.enabled,
    },
    {
      title: 'Nickname',
      style: { minWidth: '112px', textAlign: 'left' },
      sort: false,
      showHeader: tableHeaderState?.[8]?.enabled,
    },
    {
      title: 'Total',
      style: { minWidth: '97px' },
      sort: false,
      sortProps: {
        sortDirection:
          selectedHeader === 'totalAmount' ? sortDirection : undefined,
        ariaLabel: 'Sort by total amount',
        active: selectedHeader === 'totalAmount',
        direction: sortDirection,
        padding: 'none',
        onClick: () => {
          setPHRequest({
            ...pHRequest,
            sortBy: 'totalAmount',
            sortOrder: sortDirection === 'asc' ? 'desc' : 'asc',
          });
        },
        style: { paddingLeft: '0px' },
      },
      showHeader: tableHeaderState?.[9]?.enabled,
    },
  ];
};

export const PHTableHeaderValues = () => {
  const tableHeaderValuesLocalStorage =
    getWindow()?.sessionStorage?.getItem('tableHeaders') ?? '';

  let tableHeaderValues;
  try {
    tableHeaderValues = tableHeaderValuesLocalStorage
      ? JSON.parse(tableHeaderValuesLocalStorage)
      : undefined;
  } catch (jsonParseError) {
    tableHeaderValues = undefined;
  }

  return [
    {
      columnName: 'Product Images',
      enabled: tableHeaderValues?.[0]?.enabled ?? true,
      showToggle: true,
    },
    {
      columnName: 'Order/Receipt Number',
      enabled: tableHeaderValues?.[1]?.enabled ?? true,
      showToggle: true,
    },
    {
      columnName: 'Date',
      enabled: tableHeaderValues?.[2]?.enabled ?? true,
      showToggle: true,
    },
    {
      columnName: 'Origin (Location of Purchase)',
      enabled: tableHeaderValues?.[3]?.enabled ?? false,
      showToggle: true,
    },
    {
      columnName: 'Project Name',
      enabled:
        FeatureSwitches?.enableProjectName &&
        (tableHeaderValues?.[4]?.enabled ?? false),
      showToggle: FeatureSwitches?.enableProjectName ?? true,
    },
    {
      columnName: 'PO/Job Name',
      enabled: tableHeaderValues?.[5]?.enabled ?? true,
      showToggle: true,
    },
    {
      columnName: 'Payment',
      enabled: tableHeaderValues?.[6]?.enabled ?? true,
      showToggle: true,
    },
    {
      columnName: 'Purchaser/Buyer',
      enabled: tableHeaderValues?.[7]?.enabled ?? true,
      showToggle: true,
    },
    {
      columnName: 'Nickname (Payment Nickname)',
      enabled: tableHeaderValues?.[8]?.enabled ?? false,
      showToggle: true,
    },
    {
      columnName: 'Total',
      enabled: tableHeaderValues?.[9]?.enabled ?? true,
      showToggle: true,
    },
  ];
};
